/**
 * Orchestrator. Decodes an image, runs the analysis pipeline (via a callback
 * the caller provides), and returns a JSON-serializable report.
 *
 * trace-viewer doesn't depend on opencv directly. The caller supplies an
 * `analyze` function that takes `ImageData` and returns an `AnalysisResult`
 * (sync or async). The recommended pattern is to run `analyze` on a Web
 * Worker because opencv.js is ~10 MB and its parse + WASM init will hitch
 * the main thread.
 */

import { decodeImageForAnalysis, makeFileInfo } from './decode';
import type { AnalysisResult } from './analyze';
import type { ImageProbeReport, ImageWarning } from './types';

export interface ImageProbeOptions {
  /**
   * Runs the actual feature/quality/uniqueness analysis on decoded pixels.
   * Most callers should make this dispatch to a Web Worker — see the
   * tools-app for an example.
   */
  analyze: (imageData: ImageData) => AnalysisResult | Promise<AnalysisResult>;
  /**
   * Called once basic file/dimension info is available, before `analyze`
   * runs. Lets the UI render something immediately on slow first runs.
   */
  onDecoded?: (partial: ImageProbeReport) => void;
}

export async function probeMarkerImage(
  input: File | Blob,
  options: ImageProbeOptions,
): Promise<ImageProbeReport> {
  const t0 = performance.now();
  const warnings: ImageWarning[] = [];

  let decoded;
  try {
    decoded = await decodeImageForAnalysis(input);
  } catch (err) {
    return errorReport(input, 'decode-failed', err, warnings, t0);
  }

  const fileInfo = makeFileInfo(input, decoded);
  const partial: ImageProbeReport = {
    schemaVersion: 1,
    file: fileInfo,
    features: null,
    uniqueness: null,
    quality: null,
    bailout: null,
    warnings,
    analysisMs: 0,
  };
  options.onDecoded?.(partial);

  try {
    const analysis = await options.analyze(decoded.data);
    return {
      ...partial,
      features: analysis.features,
      uniqueness: analysis.uniqueness,
      quality: analysis.quality,
      analysisMs: performance.now() - t0,
    };
  } catch (err) {
    // We can't reliably distinguish "opencv failed to load" from "analysis
    // crashed mid-flight" from this side. The caller's `analyze` function
    // should produce a descriptive error message if it can.
    const message = err instanceof Error ? err.message : String(err);
    const bailout: ImageProbeReport['bailout'] =
      /opencv|wasm|runtime/i.test(message) ? 'opencv-load-failed' : 'analysis-failed';
    return {
      ...partial,
      bailout,
      warnings: [...warnings, warningFromError(bailout, err)],
      analysisMs: performance.now() - t0,
    };
  }
}

function errorReport(
  input: File | Blob,
  bailout: ImageProbeReport['bailout'],
  err: unknown,
  warnings: ImageWarning[],
  t0: number,
): ImageProbeReport {
  const name = input instanceof File ? input.name : undefined;
  return {
    schemaVersion: 1,
    file: {
      format: 'unknown',
      mimeType: input.type || 'application/octet-stream',
      fileSize: input.size,
      width: 0,
      height: 0,
    },
    features: null,
    uniqueness: null,
    quality: null,
    bailout,
    warnings: [...warnings, warningFromError(bailout ?? 'analysis-failed', err, name)],
    analysisMs: performance.now() - t0,
  };
}

function warningFromError(code: string, err: unknown, detail?: string): ImageWarning {
  const message = err instanceof Error ? err.message : String(err);
  return { code, message, detail };
}
